import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { BackOsom } from "@/components/BackOsom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Eye, EyeOff, MailCheck, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetError, setResetError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setResetEmail(email.includes("@") ? email : "");
    setResetDone(false);
    setResetError("");
    setResetOpen(true);
  };

  const onResetSubmit = async (e) => {
    e.preventDefault();
    setResetLoading(true); setResetError("");
    try {
      await api.post("/auth/password-reset/request", { email: resetEmail.trim().toLowerCase() });
      setResetDone(true);
    } catch (err) {
      setResetError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 gold-gradient gold-radial relative overflow-hidden">
        <div className="relative z-10 flex items-center justify-between">
          <Logo />
          <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="h-4 w-4" />Retour à l'accueil
          </Link>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#C5A059] mb-3">SCIC SAS · Outre-mer</div>
          <h2 className="text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Le groupement d&apos;employeurs coopératif des territoires d&apos;Outre-mer.
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Mutualisez l&apos;emploi, partagez les compétences. O&apos;SCOP GE simplifie vos mises à disposition, conventions et facturation au coût réel.
          </p>
          <div className="mt-10 flex gap-6">
            {["Conventions", "Missions", "GED ESS", "KPI temps réel"].map((k) => (
              <div key={k} className="text-sm">
                <div className="h-1 w-8 bg-[#C5A059] mb-2" />
                <div className="text-slate-700 font-medium">{k}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-500">© 2026 OBJECTIF SCOP OUTREMER</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center justify-between">
            <Logo />
            <Link to="/" data-testid="login-back-home-mobile" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft className="h-4 w-4" />Retour
            </Link>
          </div>
          <BackOsom testId="login-back-osom" className="mb-8" />
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Connexion</h1>
          <p className="text-slate-500 mt-2">Accédez à votre espace O&apos;SCOP GE.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email professionnel</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password" type={showPassword ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="h-11 pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#C5A059] transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="text-right">
              <button
                type="button"
                onClick={openReset}
                data-testid="login-forgot-password"
                className="text-sm text-[#C5A059] hover:text-[#A88846] font-medium transition-colors"
              >
                Mot de passe oublié ?
              </button>
            </div>

            <Button
              type="submit" disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-[#C5A059] hover:bg-[#A88846] text-white font-medium"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </Button>

            <div className="text-center text-sm text-slate-500">
              Pas encore de compte ?{" "}
              <Link to="/register" data-testid="login-go-register" className="text-[#C5A059] hover:text-[#A88846] font-medium">
                Créer un compte
              </Link>
            </div>
          </form>

          <Dialog open={resetOpen} onOpenChange={setResetOpen}>
            <DialogContent data-testid="forgot-password-dialog">
              <DialogHeader>
                <DialogTitle>Mot de passe oublié</DialogTitle>
                <DialogDescription>
                  Saisissez votre email professionnel. Votre demande sera transmise à un administrateur, qui validera la réinitialisation.
                </DialogDescription>
              </DialogHeader>

              {resetDone ? (
                <div className="space-y-4">
                  <div
                    className="flex items-start gap-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3"
                    data-testid="forgot-password-success"
                  >
                    <MailCheck className="h-5 w-5 mt-0.5 shrink-0" />
                    <p>
                      Si un compte existe pour cet email, une demande de réinitialisation sera traitée par un administrateur.
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => setResetOpen(false)}
                    data-testid="forgot-password-close"
                    className="w-full h-11 bg-[#C5A059] hover:bg-[#A88846] text-white font-medium"
                  >
                    Fermer
                  </Button>
                </div>
              ) : (
                <form onSubmit={onResetSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email professionnel</Label>
                    <Input
                      id="reset-email" type="email" required value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      data-testid="forgot-password-email"
                      className="h-11"
                    />
                  </div>

                  {resetError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="forgot-password-error">
                      {resetError}
                    </div>
                  )}

                  <Button
                    type="submit" disabled={resetLoading}
                    data-testid="forgot-password-submit"
                    className="w-full h-11 bg-[#C5A059] hover:bg-[#A88846] text-white font-medium"
                  >
                    {resetLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer la demande"}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
